import { useState, useRef, useEffect } from 'react'
import { useChatStore } from '@/store/chatStore'

// ─────────────────────────────────────────────
// ChatInput
// Textarea + send button at the bottom of panel.
// Enter = send. Shift+Enter = newline.
// Disabled while AI is streaming.
// Auto-grows up to 5 lines then scrolls.
// ─────────────────────────────────────────────

interface Props {
  onSend: (message: string) => void
  isConnected: boolean
  canEmailTranscript: boolean
  onEmailTranscript: () => void
}

export function ChatInput({ onSend, isConnected, canEmailTranscript, onEmailTranscript }: Props) {
  const [value, setValue]   = useState('')
  const isStreaming          = useChatStore((s) => s.isStreaming)
  // Block input until SignalR is ready — prevents first message from firing before connection
  const isDisabled           = isStreaming || !isConnected
  const textareaRef          = useRef<HTMLTextAreaElement>(null)

// Auto-focus textarea only on first connection — not on every reconnect
const hasFocusedOnce = useRef(false)

// Focus on first connection only
useEffect(() => {
  if (isConnected && !hasFocusedOnce.current && textareaRef.current) {
    textareaRef.current.focus()
    hasFocusedOnce.current = true
  }
}, [isConnected])

// Re-focus after every AI reply completes
useEffect(() => {
  if (!isStreaming && hasFocusedOnce.current && textareaRef.current) {
    textareaRef.current.focus()
  }
}, [isStreaming])

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  function handleSend() {
    const trimmed = value.trim()
    if (!trimmed || isDisabled) return
    onSend(trimmed)
    setValue('')
    // Reset textarea height after send
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setValue(e.target.value)
    // Auto-grow textarea height
    const el = e.target
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }

  return (
    <div className="
      px-4 py-3 border-t border-[var(--color-border)]
      bg-[var(--color-panel)]
    ">
      <div className="
        flex items-end gap-2 rounded-xl border
        border-[var(--color-border)]
        bg-[var(--color-input-bg)]
        px-3 py-2
        focus-within:ring-2 focus-within:ring-[var(--color-brand)]
        focus-within:border-[var(--color-brand)]
        transition-all
      ">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          placeholder={!isConnected ? 'Connecting...' : isStreaming ? 'Responding...' : 'Ask a question...'}
          rows={1}
          className="
            flex-1 resize-none bg-transparent outline-none
            text-sm text-[var(--color-text)]
            placeholder:text-[var(--color-text-sub)]
            disabled:cursor-not-allowed
            max-h-[120px] leading-relaxed
          "
        />
        <button
          onClick={handleSend}
          disabled={!value.trim() || isDisabled}
          className="
            flex-shrink-0 w-8 h-8 rounded-lg
            bg-[var(--color-brand)] hover:bg-[var(--color-brand-hover)]
            disabled:opacity-40 disabled:cursor-not-allowed
            flex items-center justify-center
            transition-colors
          "
        >
          {/* Send arrow icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="white"
            className="w-4 h-4"
          >
            <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
          </svg>
        </button>
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <p className="text-[10px] text-[var(--color-text-sub)]">
          Shift+Enter for new line · Enter to send
        </p>
        {canEmailTranscript && (
          <button
            onClick={onEmailTranscript}
            title="Email transcript"
            className="
              flex items-center gap-1 text-[10px]
              text-[var(--color-text-sub)]
              hover:text-[var(--color-brand)]
              transition-colors
            "
          >
            {/* Mail icon */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
              style={{ width: 12, height: 12 }}>
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
            Email transcript
          </button>
        )}
      </div>
    </div>
  )
}